import React, { useState, useEffect } from 'react';
import { FiClock, FiAlertCircle, FiSearch, FiFilter } from 'react-icons/fi';
import axios from 'axios';
import { API_ENDPOINTS } from '../../utils/api';

const ExpiryTracking = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    expired: 0,
    expiring: 0,
    warning: 0,
    safe: 0
  });

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(API_ENDPOINTS.getExpiryTracking, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.materials) {
        setMaterials(response.data.materials);
        if (response.data.stats) {
          setStats(response.data.stats);
        }
      } else {
        // Fallback if response format is different
        setMaterials(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching materials:', error);
      // Set empty state on error
      setMaterials([]);
      setStats({
        total: 0,
        expired: 0,
        expiring: 0,
        warning: 0,
        safe: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const getExpiryStatus = (daysUntilExpiry) => {
    if (daysUntilExpiry < 0) return { status: 'expired', color: 'red', text: 'Expired', bgColor: 'bg-red-100', textColor: 'text-red-800' };
    if (daysUntilExpiry <= 30) return { status: 'expiring', color: 'red', text: 'Expiring Soon', bgColor: 'bg-red-100', textColor: 'text-red-800' };
    if (daysUntilExpiry <= 90) return { status: 'warning', color: 'yellow', text: 'Warning', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' };
    return { status: 'safe', color: 'green', text: 'Safe', bgColor: 'bg-green-100', textColor: 'text-green-800' };
  };

  const filteredMaterials = materials.filter(material => {
    const matchesSearch = 
      material.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (material.code && material.code.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (material.batchNumber && material.batchNumber.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || material.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <FiClock className="text-blue-600" />
            Expiry Tracking
          </h1>
          <p className="text-gray-600 mt-1">Monitor material expiry dates and manage inventory</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Materials</p>
              <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
            </div>
            <FiClock className="text-blue-600 text-2xl" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Expired</p>
              <p className="text-2xl font-bold text-red-600">{stats.expired}</p>
            </div>
            <FiAlertCircle className="text-red-600 text-2xl" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Expiring Soon</p>
              <p className="text-2xl font-bold text-red-600">{stats.expiring}</p>
            </div>
            <FiAlertCircle className="text-red-600 text-2xl" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Warning</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.warning}</p>
            </div>
            <FiAlertCircle className="text-yellow-600 text-2xl" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Safe</p>
              <p className="text-2xl font-bold text-green-600">{stats.safe}</p>
            </div>
            <FiClock className="text-green-600 text-2xl" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex gap-4 mb-6">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search materials, codes, batches..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="expired">Expired</option>
            <option value="expiring">Expiring Soon</option>
            <option value="warning">Warning</option>
            <option value="safe">Safe</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Material</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Code</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Batch Number</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Quantity</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Expiry Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Days Until Expiry</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredMaterials.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-12 text-gray-500">
                      No materials found
                    </td>
                  </tr>
                ) : (
                  filteredMaterials.map((material) => {
                    const status = getExpiryStatus(material.daysUntilExpiry);
                    return (
                      <tr key={material.id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{material.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{material.code}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{material.batchNumber}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {material.quantity} {material.unit}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{material.expiryDate}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {material.daysUntilExpiry < 0 
                            ? `Expired ${Math.abs(material.daysUntilExpiry)} days ago`
                            : `${material.daysUntilExpiry} days`
                          }
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit ${status.bgColor} ${status.textColor}`}>
                            {status.text === 'Expired' || status.text === 'Expiring Soon' ? <FiAlertCircle /> : null}
                            {status.text}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpiryTracking;

